import { DateTimePicker12 } from '../components/TimePicker12';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { checkVisitAvailability, createVisit, getProperty } from '../api';
import { useAuth } from '../auth';
import FormWizard from '../components/FormWizard';
import AuthToBook from '../components/AuthToBook';
import StarRating from '../components/StarRating';
import ItemRatings from '../components/ItemRatings';
import RelatedItems from '../components/RelatedItems';
import PhotoGallery from '../components/PhotoGallery';
import MapLink from '../components/MapLink';
import { SpecificationsTable } from '../components/ItemPoliciesAndSpecs';

// 03_requirements_missing.md story 7 - the structured site address is stored as flat
// attributes.site* keys.
function siteAddressOf(attributes = {}) {
  return {
    line1: attributes.siteAddressLine1 || '', line2: attributes.siteAddressLine2 || '',
    area: attributes.siteArea || '', city: attributes.siteCity || '', state: attributes.siteState || '',
    pincode: attributes.sitePincode || '',
    lat: attributes.siteLat ?? null, lng: attributes.siteLng ?? null,
    formatted: attributes.siteFormatted || '',
    // story 9 - the API coarsens the location for anyone who isn't the listing's owner/an admin.
    approximate: !!attributes.siteApproximate
  };
}

// v5_fix_issues #8 - name / email / phone come from the account, not the form.
const emptyForm = {
  visitDateTime: '',
  notes: ''
};

export default function ItemDetail() {
  const { id } = useParams();
  const location = useLocation();
  // homepage/user_experience.md #3 - the filtered/sorted browse URL the customer came from.
  const backToListings = location.state?.from || '/';
  const { user, isAuthenticated } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  // Story 6: null = not checked yet, true/false = the last checked slot's availability.
  const [slotAvailable, setSlotAvailable] = useState(null);
  const [checkingSlot, setCheckingSlot] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getProperty(id)
      .then((data) => {
        if (!cancelled) setItem(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Property not found.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);


  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (field === 'visitDateTime') setSlotAvailable(null); // stale until re-checked
  };

  // Story 6: checked on blur rather than on every keystroke - one request per deliberate change,
  // not per character typed into the datetime picker.
  const handleDateBlur = async () => {
    if (!form.visitDateTime) return;
    setCheckingSlot(true);
    try {
      const available = await checkVisitAvailability(id, new Date(form.visitDateTime).toISOString());
      setSlotAvailable(available);
    } catch {
      setSlotAvailable(null); // couldn't check - don't block submission on a check failure
    } finally {
      setCheckingSlot(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (slotAvailable === false) {
      setSubmitError('This slot is already booked - please pick another time.');
      return;
    }
    if (!user?.phone) {
      setSubmitError('Add a phone number to your account before requesting a visit.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      // DateTimePicker12 gives "YYYY-MM-DDTHH:mm" with no timezone - new Date() parses it as local
      // time, and toISOString() gives the backend an unambiguous instant (RealEstateVisit_user_
      // stories_v2.md story 8: exact time, not just a date).
      const { visitDateTime, ...rest } = form;
      const res = await createVisit({ ...rest, propertyId: id, visitDate: new Date(visitDateTime).toISOString() });
      setConfirmation(res);
    } catch (err) {
      setSubmitError(err.response?.data?.msg || 'Unable to submit visit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner animation="border" role="status" />;
  if (loadError) {
    return (
      <Alert variant="light" className="mt-3">
        <p className="mb-2">{loadError} It may have been taken down or already let.</p>
        <Button as={Link} to={backToListings} variant="primary">Browse all properties</Button>
      </Alert>
    );
  }
  if (!item) return null;

  const identityBlock = (
    <div className="mb-3 small">
      <div><span className="text-muted">Name:</span> {user?.name}</div>
      <div><span className="text-muted">Email:</span> {user?.email}</div>
      <div>
        <span className="text-muted">Phone:</span>{' '}
        {user?.phone ? user.phone : <Link to="/account">Add a phone number to your account</Link>}
      </div>
    </div>
  );

  // v6 stepper forms (Part D / §D3) - the visit request is a 2-step wizard in the right column.
  const bookingSteps = [
    {
      key: 'datetime',
      title: 'Date & time',
      validate: (v) => {
        if (!v.visitDateTime) return { visitDateTime: 'Pick a preferred date and time.' };
        if (slotAvailable === false) return { visitDateTime: 'This slot is already booked - please pick another time.' };
        return null;
      },
      render: () => (
        <Form.Group className="mb-2" controlId="visit-datetime">
          <Form.Label>Preferred date &amp; time</Form.Label>
          {/* DateTimePicker12 is a composite (a date input + TimePicker12's own 3 selects), not
              a single control `controlId` can auto-associate via htmlFor - without an explicit
              aria-label its sub-controls fall back to the generic "Date and time date"/"...time"
              default, disconnected from this visible "Preferred date & time" label. */}
          <DateTimePicker12
            aria-label="Preferred date & time"
            required
            min={new Date().toISOString().slice(0, 16)}
            value={form.visitDateTime}
            onChange={handleChange('visitDateTime')}
            onBlur={handleDateBlur}
            isInvalid={slotAvailable === false}
          />
          {checkingSlot && <Form.Text muted>Checking availability…</Form.Text>}
          {slotAvailable === false && (
            <Form.Control.Feedback type="invalid">
              This slot is already booked - please pick another time.
            </Form.Control.Feedback>
          )}
          {slotAvailable === true && <Form.Text className="text-success">✓ This slot is available</Form.Text>}
        </Form.Group>
      )
    },
    {
      key: 'review',
      title: 'Review',
      render: () => (
        <>
          {identityBlock}
          <Form.Group className="mb-3" controlId="visit-notes">
            <Form.Label>Anything the owner should know?</Form.Label>
            <Form.Control as="textarea" rows={2} value={form.notes} onChange={handleChange('notes')} />
          </Form.Group>
        </>
      )
    }
  ];

  const renderVisitPreview = () => (
    <div>
      <h5>{item.name}</h5>
      <dl className="row small">
        <dt className="col-4">When</dt>
        <dd className="col-8">{form.visitDateTime ? new Date(form.visitDateTime).toLocaleString() : '—'}</dd>
        <dt className="col-4">Name</dt><dd className="col-8">{user?.name}</dd>
        <dt className="col-4">Phone</dt><dd className="col-8">{user?.phone || '—'}</dd>
      </dl>
      {form.notes && <p className="small"><span className="text-muted">Notes:</span> {form.notes}</p>}
      <p className="text-muted small mb-0">No payment is taken for a visit.</p>
    </div>
  );

  return (
    <div>
      <Link to={backToListings} className="btn btn-primary btn-sm mb-3 d-inline-flex align-items-center gap-2">
        <span aria-hidden="true" style={{ fontSize: '1.1em', lineHeight: 1 }}>←</span> Back to listings
      </Link>

      {!isAuthenticated && !confirmation && (
        <AuthToBook as="banner" action="request a visit" />
      )}

      <Row className="g-4">
        <Col md={6}>
          <PhotoGallery images={item.images} alt={item.name} />
          <h2 className="mt-3">{item.name}</h2>
          <p className="text-muted">
            {item.category} &middot; {item.location}
          </p>
          <p>{item.description}</p>
          {item.ratingCount > 0 && (
            <div className="mb-2"><StarRating value={item.ratingAvg} count={item.ratingCount} size="1.15rem" /></div>
          )}
          <div className="mb-2">
            {item.attributes?.bedrooms != null && <Badge bg="secondary" className="me-1">{item.attributes.bedrooms} BHK</Badge>}
            {item.attributes?.bathrooms != null && <Badge bg="secondary" className="me-1">{item.attributes.bathrooms} bath</Badge>}
            {item.attributes?.areaSqft != null && <Badge bg="secondary" className="me-1">{item.attributes.areaSqft} sqft</Badge>}
            {(item.attributes?.amenities || []).map((a) => (
              <Badge bg="light" text="dark" className="me-1 border" key={a}>
                {a}
              </Badge>
            ))}
          </div>
          <h4>
            ₹{item.price}/{item.priceUnit}
          </h4>
          <SpecificationsTable specifications={item.specifications} />
          {/* 03_requirements_missing.md story 9 - the owner's number is never shown to a customer.
              The visit workflow is the contact channel: request a visit, then the owner or their
              agent calls around the visit time. */}
          <Alert variant="light" className="small mb-3 border">
            <strong>How a visit works:</strong> request a site visit below and pick a time. The
            owner or their assigned agent confirms it and calls you close to the visit.
          </Alert>
          {/* maps_on_demand_and_location_reuse.md - no inline embed; the map opens in a popup on
              click. story 9 - customers only see the approximate area, not the exact address. */}
          <div className="mb-3">
            <MapLink
              address={siteAddressOf(item.attributes)}
              fallbackText={item.location}
              title="Approximate location"
              approximate
            />
          </div>
        </Col>

        <Col md={6}>
          {confirmation ? (
            <Alert variant="success">
              <strong>{confirmation.msg}</strong>
              <p className="mb-0 mt-2">
                Track it any time on <Link to="/my-visits">My Visits</Link>.
              </p>
            </Alert>
          ) : !isAuthenticated ? (
            // fix_issues.md #10 - visit requests now require a logged-in account too (no more
            // guest checkout), so the form itself is gated the same way rather than letting a
            // guest fill it in and only find out from a 401 on submit. <AuthToBook> links carry
            // the current path so Login/Signup return the visitor right back here (v6 UX).
            <AuthToBook action="request a visit" />
          ) : item.bookingAvailability?.canBook === false ? (
            <Alert variant="light">{item.bookingAvailability.message}</Alert>
          ) : item.status !== 'available' ? (
            <Alert variant="light">This property is currently unavailable for visits.</Alert>
          ) : (
            <div>
              <h4>Request a site visit</h4>
              <p className="text-muted small mb-3">
                No payment is taken for a visit. Rent is arranged directly with the property owner.
              </p>
              {submitError && <Alert variant="danger">{submitError}</Alert>}
              <FormWizard
                steps={bookingSteps}
                values={form}
                onChange={setForm}
                onSubmit={handleSubmit}
                submitting={submitting}
                submitLabel="Request visit"
                renderPreview={renderVisitPreview}
              />
            </div>
          )}
        </Col>
      </Row>

      <ItemRatings itemId={item._id} />
      <RelatedItems itemId={item._id} category={item.category} />
      <div className="mt-4"><Link to={backToListings} className="btn btn-primary d-inline-flex align-items-center gap-2"><span aria-hidden="true">←</span> Back to listings</Link></div>
    </div>
  );
}
